import React, { ReactElement, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import * as Styled from './styles';
import { Question } from './Question';
import Divider from '../../assets/divider.inline.svg';
import { useVisibilityDetection } from '../../hooks/useVisibilityDetection';

interface QuestionData {
  title: string;
  description: string | ReactNode;
}

const DATA: QuestionData[] = [
  {
    title: 'Does SketchWow run on Windows and Mac computers?',
    description:
      "Absolutely! Also, you don't have to install any third-party software to make SketchWow work on your computer. Just download, install and go.",
  },
  {
    title: 'Do I have to install anything?',
    description:
      'Yes. SketchWow is a powerful software application that installs on your computer. We support both Windows 32 or 64 bit and Mac (OSX) operating systems.',
  },
  {
    title: 'Does SketchWow require an Internet connection?',
    description:
      'SketchWow does not run in your web browser. That means, you do not have to be constantly connected to the Internet when using SketchWow. When you open SketchWow, the software will validate your software license key using the Internet. After that, you can unplug and start sketching without requiring an Internet or WiFi connection. No more lost diagrams due to lag or your WiFi timing out!',
  },
  {
    title: 'Are all the shapes & icons royalty-free?',
    description:
      "They sure are! The shapes and icons included within the software are created exclusively by our in house design team. We don't source images from third-party sites and license them. Rest assured, you can use all of the shapes and images in SketchWow Standard for personal/work related projects with no attribution required. If you do client or freelance work (or plan to sell/offer your designs to a third party), SketchWow Pro includes a Full Commercial Rights license.",
  },
  {
    title: 'How can I export my sketches?',
    description: 'You can export to JPG, PNG, PDF or Animated GIF right within SketchWow.',
  },
  {
    title: 'Can I import images and graphics?',
    description:
      "You betcha! It's super simple too. Just drag-and-drop any image from your folder/directory on your computer right onto the canvas in SketchWow. Done. Next.",
  },
  {
    title: 'Can I use custom dimensions?',
    description:
      'Of course! You can completely customize the width and height of the canvas. This makes it easy when working on different diagrams for different purposes (example: dropping a diagram into a Twitter post versus adding a sketch to a PowerPoint).',
  },
  {
    title: 'Do you include instructions to help me get started?',
    description: "Yes. But, you probably won't need them. SketchWow was designed to be purposely uncomplicated and easy to use.",
  },
  {
    title: 'Is this safe & legit to order?',
    description:
      'Yes! We want you to be 100% thrilled with SketchWow. We use leading payment processors like Stripe and PayPal. Also, we give you a 30 day moneyback guarantee. This way, you can kick the tires on SketchWow for a full month before you make a final decision.',
  },
  {
    title: 'Does this include support?',
    description:
      "Crickets... we have a full-time Help Desk to serve you, but truth be told, it's not very busy. The biggest request we get is \"I can't find my software license key\". Other than that, it's pretty quiet around here.",
  },
  {
    title: 'Is there a monthly fee to use SketchWow?',
    description: (
      <>
        You have a couple of options. If you would prefer to go month-to-month, we have a monthly pay-as-you-go option.
        <br />
        <br />
        If you'd prefer to lock-in a large discount and use our annual payment option, you can do that too. There are no monthly fees with
        the annual (yearly) payment plan.
        <br />
        <br />
        Your investment in SketchWow helps us (and motivates us) to push forward with new developments, updates, features and improvements.
      </>
    ),
  },
  {
    title: 'Do I get software updates included?',
    description:
      'Oh Yes! Some software developers try and "ding" you every 6 months when a new version gets released. That\'s not our style. As a valued customer of SketchWow, you\'ll get any future updates, improvements and enhancements (for the original account plan you purchased) at no additional cost. In other words, no surprises.',
  },
  {
    title: 'Will my sketches really “Wow” my audience using this?',
    description:
      'Give it a try! People love how unique and original diagrams, workflows and maps look using SketchWow. Don’t be surprised if you get asked, “Hey. How did you create that??”. SketchWow is built on simplicity, getting the conversation started and provides you with a more simple way to visually communicate and explain your thoughts and ideas.',
  },
  {
    title: 'Is there a money back guarantee?',
    description:
      'There sure is. We want you to love SketchWow. Give SketchWow a whirl for a full month. If you are not 100% thrilled for whatever reason, let us know within 30 days and you’ll get all your money back.',
  },
];

export const FAQ = (): ReactElement => {
  const FAQRef = useRef(null);
  const { isVisible } = useVisibilityDetection(FAQRef);
  const preparedData = useMemo(
    () =>
      DATA.map((item) => ({
        ...item,
        id: uniqueId(),
      })),
    [],
  );
  const splitedData = useMemo(
    () => ({
      left: preparedData.filter((_, index) => !(index % 2)),
      right: preparedData.filter((_, index) => index % 2),
    }),
    [preparedData],
  );

  const [activeQuestionId, setActiveQuestionId] = useState(splitedData.left[0].id);

  const handleActiveQuestionId = useCallback(
    (id: string) => {
      setActiveQuestionId(id !== activeQuestionId ? id : '');
    },
    [activeQuestionId],
  );

  return (
    <Styled.Wrapper isVisible={isVisible} ref={FAQRef}>
      <Styled.Heading>
        <Styled.Title>A few frequently asked questions</Styled.Title>
      </Styled.Heading>
      <Styled.Questions>
        <Styled.QuestionsColumn>
          {splitedData.left.map(({ title, description, id }) => (
            <Question
              activeId={activeQuestionId}
              description={description}
              id={id}
              title={title}
              onChange={handleActiveQuestionId}
              key={id}
            />
          ))}
        </Styled.QuestionsColumn>
        <Styled.QuestionsColumn>
          {splitedData.right.map(({ title, description, id }) => (
            <Question
              activeId={activeQuestionId}
              description={description}
              id={id}
              title={title}
              onChange={handleActiveQuestionId}
              key={id}
            />
          ))}
        </Styled.QuestionsColumn>
      </Styled.Questions>
      <Divider />
    </Styled.Wrapper>
  );
};
